import React from "react";
import { Box, Card, CardContent, Chip, Stack, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import AssignmentTurnedInRoundedIcon from "@mui/icons-material/AssignmentTurnedInRounded";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import LockOpenRoundedIcon from "@mui/icons-material/LockOpenRounded";
import AdminHeader from "../../AdminHeader";

const TEXT_PRIMARY = "#111827";
const TEXT_MUTED = "#667085";

const roleRows = [
  {
    id: 1,
    role: "Admin",
    devices: "Full",
    telemetry: "Full",
    users: "Full",
  },
  {
    id: 2,
    role: "Operator",
    devices: "Edit",
    telemetry: "View",
    users: "None",
  },
  {
    id: 3,
    role: "Viewer",
    devices: "View",
    telemetry: "View",
    users: "None",
  },
];

const getPermissionStyle = (value) => {
  switch (value) {
    case "Full":
      return {
        color: "#166534",
        backgroundColor: "#dcfce7",
        border: "1px solid #86efac",
      };
    case "Edit":
      return {
        color: "#9a3412",
        backgroundColor: "#ffedd5",
        border: "1px solid #fdba74",
      };
    case "View":
      return {
        color: "#1d4ed8",
        backgroundColor: "#dbeafe",
        border: "1px solid #93c5fd",
      };
    case "None":
      return {
        color: "#475569",
        backgroundColor: "#f1f5f9",
        border: "1px solid #cbd5e1",
      };
    default:
      return {};
  }
};

const permissionCount = roleRows.reduce(
  (count, row) =>
    count +
    ["devices", "telemetry", "users"].filter((field) => row[field] !== "None")
      .length,
  0,
);

const columns = [
  {
    field: "role",
    headerName: "Role",
    flex: 1,
    minWidth: 180,
    renderCell: (params) => (
      <Stack spacing={0.35} justifyContent="center">
        <Typography
          sx={{
            fontWeight: 700,
            color: TEXT_PRIMARY,
            letterSpacing: "-0.01em",
          }}
        >
          {params.value}
        </Typography>
        <Typography variant="body2" sx={{ color: TEXT_MUTED }}>
          Platform access role
        </Typography>
      </Stack>
    ),
  },
  {
    field: "devices",
    headerName: "Devices",
    flex: 1,
    minWidth: 130,
    renderCell: (params) => (
      <Chip
        label={params.value}
        size="small"
        sx={{
          borderRadius: 2,
          fontWeight: 600,
          ...getPermissionStyle(params.value),
        }}
      />
    ),
  },
  {
    field: "telemetry",
    headerName: "Telemetry",
    flex: 1,
    minWidth: 130,
    renderCell: (params) => (
      <Chip
        label={params.value}
        size="small"
        sx={{
          borderRadius: 2,
          fontWeight: 600,
          ...getPermissionStyle(params.value),
        }}
      />
    ),
  },
  {
    field: "users",
    headerName: "Users",
    flex: 1,
    minWidth: 130,
    renderCell: (params) => (
      <Chip
        label={params.value}
        size="small"
        sx={{
          borderRadius: 2,
          fontWeight: 600,
          ...getPermissionStyle(params.value),
        }}
      />
    ),
  },
  {
    field: "action",
    headerName: "",
    width: 70,
    align: "center",
    headerAlign: "center",
    sortable: false,
    filterable: false,
    renderCell: () => <ChevronRightIcon sx={{ color: "#64748b" }} />,
  },
];

export default function RoleMatrix({ title }) {
  const pageTitle = title || "Roles & Permissions";
  const fullAccessRoles = roleRows.filter(
    (row) => row.devices === "Full" && row.telemetry === "Full" && row.users === "Full",
  ).length;

  return (
    <Box
      sx={{
        background:
          "radial-gradient(circle at top left, rgba(14, 165, 233, 0.14), transparent 30%), radial-gradient(circle at top right, rgba(16, 185, 129, 0.12), transparent 28%), linear-gradient(180deg, #f8fbff 0%, #eef4f8 100%)",
        p: { xs: 0, md: 1 },
      }}
    >
      <AdminHeader title={pageTitle} />

      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          mb: 4,
          mt: { xs: 2, md: 3 },
          mx: { xs: 1.5, md: 0.5 },
          px: { xs: 2, md: 3 },
          py: { xs: 3, md: 4 },
          borderRadius: 4,
          background:
            "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
          boxShadow: "0 30px 80px rgba(15, 23, 42, 0.25)",
        }}
      >
        <Stack spacing={1.1}>
          <Typography
            sx={{
              fontWeight: 700,
              lineHeight: 1.1,
              letterSpacing: "-0.03em",
              color: "#fff",
              fontSize: {
                xs: 28,
                // sm: "2.2rem",
                md: 40,
              },
            }}
          >
            Role Matrix
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "#b4b0b0ff", maxWidth: 720 }}
          >
            Review platform roles and compare access levels for devices,
            telemetry, and user administration.
          </Typography>
        </Stack>

        <Box
          display="grid"
          gridTemplateColumns={{
            xs: "1fr",
            sm: "1fr 1fr",
            md: "1fr 1fr 1fr 1fr",
          }}
          gap={2}
          mt={2}
        >
          {[
            {
              title: "Total Roles",
              value: roleRows.length,
              icon: <GroupsRoundedIcon color="action" />,
              bg: "#f1f5f9",
            },
            {
              title: "Full Access Roles",
              value: fullAccessRoles,
              icon: <AdminPanelSettingsRoundedIcon sx={{ color: "#6d28d9" }} />,
              bg: "#f5f3ff",
            },
            {
              title: "Permission Grants",
              value: permissionCount,
              icon: <AssignmentTurnedInRoundedIcon sx={{ color: "#065f46" }} />,
              bg: "#ecfdf5",
            },
            {
              title: "Managed Areas",
              value: 3,
              icon: <LockOpenRoundedIcon sx={{ color: "#dc2626" }} />,
              bg: "#fff5f5",
            },
          ].map((s, i) => (
            <Card
              key={i}
              elevation={0}
              sx={{
                borderRadius: 4,
                p: 2,
                backgroundColor: "rgba(255,255,255,0.08)",
                border: "1px solid rgba(255,255,255,0.15)",
                backdropFilter: "blur(12px)",
                color: "#fff",
                transition: "all 0.25s ease",
                "&:hover": {
                  transform: "translateY(-5px)",
                  boxShadow: "0 15px 40px rgba(0,0,0,0.25)",
                },
              }}
            >
              <Box display="flex" alignItems="center">
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: 2,
                    bgcolor: s.bg,
                    mr: 2,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  {s.icon}
                </Box>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{ color: "rgba(255,255,255,0.7)", fontWeight: 500 }}
                  >
                    {s.title}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {s.value.toLocaleString()}
                  </Typography>
                </Box>
              </Box>
            </Card>
          ))}
        </Box>
      </Box>

      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        <CardContent sx={{ p: { xs: 0, md: 0 } }}>
          <Box
            sx={{
              p: { xs: 2, md: 2.5 },
              background:
                "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: "4",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                xs:15,
                md:20,
                letterSpacing: "-0.02em",
                color: "#fff",
              }}
            >
              Permission Operations
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(203,213,225,0.75)" }}>
              Compare access strength across each platform role.
            </Typography>
          </Box>

          <Box sx={{ height: 360, width: "100%" }}>
            <DataGrid
              rows={roleRows}
              columns={columns}
              hideFooter
              disableColumnMenu
              disableRowSelectionOnClick
              rowHeight={66}
              onRowClick={(params) => {
                console.log("Clicked role:", params.row.role);
              }}
              sx={{
                border: 0,
                "--DataGrid-overlayHeight": "220px",
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#fbfcfd",
                  borderBottom: "1px solid rgba(17, 24, 39, 0.08)",
                  fontSize: 13,
                  fontWeight: 800,
                  color: "#475467",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: 800,
                },
                "& .MuiDataGrid-cell": {
                  borderBottom: "1px solid rgba(17, 24, 39, 0.06)",
                  alignItems: "center",
                  outline: "none",
                },
                "& .MuiDataGrid-row": {
                  cursor: "pointer",
                  transition:
                    "background-color 160ms ease, transform 160ms ease",
                  "&:hover": {
                    backgroundColor: "#f8fafc",
                  },
                },
                "& .MuiDataGrid-footerContainer": {
                  borderTop: "1px solid rgba(17, 24, 39, 0.08)",
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
